import {
  DownloadOutlined,
  FilePdfOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import {
  dirhams,
  downloadReportPdf,
  membersCount,
  openReportPdf,
  present,
  ReportError,
  totalDue,
  weekChoices,
} from '@horof/shared';
import { Alert, App, Button, Select, Skeleton } from 'antd';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import { ReportsOverviewDocument } from './reports-data';

/**
 * The committee's reports on the phone: today's reminders (sent from this
 * phone), the late payers, and the weekly report to open or share. The
 * monthly and annual reports are on the desk (docs/changes/45–46).
 */
export function ReportsPage() {
  const [{ data, error }] = useQuery({
    query: ReportsOverviewDocument,
    requestPolicy: 'cache-and-network',
  });
  const arrears = present(data?.arrearsList?.nodes);
  const reminders = data?.remindersDue?.totalCount ?? 0;

  return (
    <div className="page">
      <h1 className="member-title">التقارير</h1>
      {error && <Alert type="error" showIcon message="تعذّر تحميل التقارير" />}
      {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}

      {data && (
        <>
          <Link to="/reports/reminders" className="card report-link">
            <span>
              <span className="section-title">تذكيرات اليوم</span>
              <span className="field-hint">
                {reminders
                  ? `${membersCount(reminders)} للتذكير اليوم`
                  : 'لا أحد يجب تذكيره اليوم'}
              </span>
            </span>
            <span className={`report-count${reminders ? ' is-due' : ''}`}>
              {reminders}
            </span>
            <LeftOutlined className="row-link-arrow" />
          </Link>

          <Link to="/reports/arrears" className="card report-link">
            <span>
              <span className="section-title">المتأخرون عن الأداء</span>
              <span className="field-hint">
                {membersCount(arrears.length)} · {dirhams(totalDue(arrears))}
              </span>
            </span>
            <LeftOutlined className="row-link-arrow" />
          </Link>

          <div className="field-hint">
            التقرير الشهري والتقرير السنوي على الحاسوب («مكتب الجمعية»،
            التقارير).
          </div>
        </>
      )}
      {/* Independent of the overview: it has its own request. */}
      <WeeklyReport />
    </div>
  );
}

/**
 * The weekly report e-mailed to the committee on Mondays (arqam doc 44), for
 * any week: income by collector, collection of the last months, late payers,
 * WhatsApp, status changes. A PDF behind the login (committee or treasurer).
 */
function WeeklyReport() {
  const { message } = App.useApp();
  const choices = useMemo(() => weekChoices(todayInMorocco()), []);
  const [end, setEnd] = useState(choices[0].end);
  const [busy, setBusy] = useState<'open' | 'download' | null>(null);
  const label = choices.find((c) => c.end === end)?.label ?? '';

  async function run(kind: 'open' | 'download') {
    setBusy(kind);
    try {
      if (kind === 'open') await openReportPdf('weekly', { end });
      else
        await downloadReportPdf('weekly', `التقرير الأسبوعي ${label}.pdf`, {
          end,
        });
    } catch (error) {
      message.error(
        error instanceof ReportError && error.status === 403
          ? 'التقرير الأسبوعي للجنة وأمين المال فقط.'
          : 'تعذّر تحميل التقرير. أعد المحاولة.',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card">
      <h2 className="section-title">التقرير الأسبوعي</h2>
      <div className="field-hint">
        المداخيل حسب المحصِّل، التحصيل في الأشهر الأخيرة، المتأخرون، رسائل واتساب،
        وتغييرات الحالة. يُرسَل كل يوم اثنين لأعضاء اللجنة بالبريد.
      </div>
      <Select
        size="large"
        value={end}
        onChange={setEnd}
        options={choices.map((c) => ({ value: c.end, label: c.label }))}
      />
      <div className="link-grid">
        <Button
          type="primary"
          size="large"
          icon={<FilePdfOutlined />}
          loading={busy === 'open'}
          onClick={() => run('open')}
        >
          فتح
        </Button>
        <Button
          size="large"
          icon={<DownloadOutlined />}
          loading={busy === 'download'}
          onClick={() => run('download')}
        >
          تحميل
        </Button>
      </div>
    </section>
  );
}

/** Today in Morocco ("YYYY-MM-DD"). */
const todayInMorocco = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Casablanca' }).format(
    new Date(),
  );
